import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Save, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminChurchInfo() {
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/church-info").then((r) => setInfo(r.data)); }, []);

  if (!info) return <div className="text-slate-500">Chargement…</div>;

  const upd = (k) => (e) => setInfo((i) => ({ ...i, [k]: e.target.value }));

  const updateSchedule = (idx, k, val) => {
    setInfo((i) => ({
      ...i,
      weekly_schedule: i.weekly_schedule.map((s, j) => j === idx ? { ...s, [k]: val } : s),
    }));
  };

  const addSlot = () => setInfo((i) => ({ ...i, weekly_schedule: [...i.weekly_schedule, { day: "", time: "", title: "" }] }));
  const removeSlot = (idx) => setInfo((i) => ({ ...i, weekly_schedule: i.weekly_schedule.filter((_, j) => j !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/church-info", info);
      setInfo(data);
      toast.success("Informations mises à jour");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-church">
      <div className="mb-8">
        <div className="text-gold-dark text-xs tracking-[0.3em] uppercase">Configuration</div>
        <h1 className="font-serif text-3xl sm:text-4xl text-deepblue-900 mt-1">Informations de l'église</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
          <h2 className="font-serif text-xl text-deepblue-900">Général</h2>
          <div><Label>Nom</Label><Input value={info.name} onChange={upd("name")} className="mt-2" data-testid="info-name" /></div>
          <div><Label>Slogan</Label><Input value={info.slogan} onChange={upd("slogan")} className="mt-2" /></div>
          <div>
            <Label>Logo (URL)</Label>
            <Input value={info.logo_url || ""} onChange={upd("logo_url")} className="mt-2" data-testid="info-logo"
              placeholder="https://…/logo.png" />
            {info.logo_url && (
              <div className="mt-3 flex items-center gap-3">
                <img src={info.logo_url} alt="Logo" className="w-16 h-16 rounded-full object-cover ring-2 ring-gold/60 bg-white" />
                <p className="text-xs text-slate-500">Aperçu du logo actuel</p>
              </div>
            )}
          </div>
          <div><Label>Adresse</Label><Input value={info.address} onChange={upd("address")} className="mt-2" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Téléphone</Label><Input value={info.phone} onChange={upd("phone")} className="mt-2" /></div>
            <div><Label>WhatsApp</Label><Input value={info.whatsapp} onChange={upd("whatsapp")} className="mt-2" placeholder="+33…" /></div>
          </div>
          <div><Label>Email</Label><Input type="email" value={info.email} onChange={upd("email")} className="mt-2" /></div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
          <h2 className="font-serif text-xl text-deepblue-900">Pasteur</h2>
          <div><Label>Nom du pasteur</Label><Input value={info.pastor_name} onChange={upd("pastor_name")} className="mt-2" /></div>
          <div><Label>Message du pasteur</Label><Textarea rows={6} value={info.pastor_message} onChange={upd("pastor_message")} className="mt-2" /></div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
          <h2 className="font-serif text-xl text-deepblue-900">Réseaux sociaux</h2>
          <div><Label>Facebook URL</Label><Input value={info.facebook_url} onChange={upd("facebook_url")} className="mt-2" /></div>
          <div><Label>YouTube URL</Label><Input value={info.youtube_url} onChange={upd("youtube_url")} className="mt-2" /></div>
          <div><Label>Instagram URL</Label><Input value={info.instagram_url} onChange={upd("instagram_url")} className="mt-2" /></div>
          <div><Label>Lien de don</Label><Input value={info.donation_url} onChange={upd("donation_url")} className="mt-2" placeholder="https://…" /></div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl text-deepblue-900">Programme hebdomadaire</h2>
            <Button size="sm" variant="outline" onClick={addSlot}><Plus className="w-4 h-4 mr-1" />Ajouter</Button>
          </div>
          <div className="space-y-3">
            {info.weekly_schedule.map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <Input className="col-span-3" placeholder="Jour" value={s.day} onChange={(e) => updateSchedule(i, "day", e.target.value)} />
                <Input className="col-span-2" placeholder="10h00" value={s.time} onChange={(e) => updateSchedule(i, "time", e.target.value)} />
                <Input className="col-span-6" placeholder="Culte du dimanche" value={s.title} onChange={(e) => updateSchedule(i, "title", e.target.value)} />
                <Button size="icon" variant="ghost" className="col-span-1" onClick={() => removeSlot(i)}><X className="w-4 h-4 text-red-600" /></Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-gold hover:bg-gold-hover text-white rounded-full px-8" data-testid="save-church-btn">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Enregistrement…" : "Enregistrer les modifications"}
        </Button>
      </div>
    </div>
  );
}
